import React, { useEffect, useState } from 'react';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { PieChart, Pie, Cell } from 'recharts';
import {
  ChartColorLegend,
  ChartLegendBogItem,
  ChartLegendGridItem,
  ChartNoDataGridItem,
  DashboardChartWrapper,
  ResponsiveContainerStyled,
} from 'pages/DashboardPage/PieCharts/styles';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { colors } from 'theme/colors';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { getFirearmsLegality } from 'requests/dashboard';
import { useTranslations } from 'hooks/useTranslations';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { EDashboardSortType } from 'models/dashboard';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

interface IFirearmsChart {
  sortType: EDashboardSortType;
  setLoader: React.Dispatch<React.SetStateAction<boolean>>;
}

const FirearmsChart = ({ sortType, setLoader }: IFirearmsChart) => {
  const navigate = useNavigate();
  const [legalityData, setLegalityData] = useState<[number, number]>();
  const [totalValue, setTotalValue] = useState(1);
  const { t } = useTranslations();
  const [chartData, setChartData] = useState<any>(undefined);
  const { isDesktop, isMobile } = useThemeBreakpoint();
  const [noData, setNoData] = useState(false);
  const [noDataText, setNoDataText] = useState<string>('');

  useEffect(() => {
    (async () => {
      setLoader(true);

      const data = await getFirearmsLegality(sortType);

      if (data?.chartValues) {
        const legality: [number, number] = [
          data.chartValues?.[0]?.value,
          data.chartValues?.[1]?.value,
        ];

        setNoData(legality.every((item) => item === 0));
        setLegalityData(legality);
        setTotalValue(legality.reduce((total, current) => total + current, 0));
      } else {
        setNoData(true);
      }

      setLoader(false);
    })();
  }, [sortType]);

  useEffect(() => {
    switch (sortType) {
      case EDashboardSortType.Day:
        setNoDataText('dashboard.chart.firearms.legality.noDataToday');
        break;
      case EDashboardSortType.Week:
        setNoDataText('dashboard.chart.firearms.legality.noData7days');
        break;
      case EDashboardSortType.Month:
        setNoDataText('dashboard.chart.firearms.legality.noData30days');
        break;
      case EDashboardSortType.Year:
        setNoDataText('dashboard.chart.firearms.legality.noDataYear');
        break;
      case EDashboardSortType.All:
      default:
        setNoDataText('dashboard.chart.firearms.legality.noDataAll');
        break;
    }
  }, [sortType]);

  useEffect(() => {
    setChartData(undefined);
    if (legalityData?.length) {
      const newData = [
        {
          id: 'success',
          label: 'Success',
          value: Number(((legalityData[0] / totalValue) * 100).toFixed(1)),
          color: colors.success400,
        },
        {
          id: 'primary',
          label: 'Primary',
          value: Number(((legalityData[1] / totalValue) * 100).toFixed(1)),
          color: colors.gray500,
        },
      ];
      setTimeout(() => setChartData(newData), 10);
    }
  }, [legalityData, totalValue]);

  const pieKey = React.useMemo(
    () => `firearms-chart-${legalityData?.join('-')}-${sortType}`,
    [legalityData, sortType],
  );

  const chartRender = (
    <Grid container sx={{ height: isDesktop ? 'fit-content' : 'auto', flexDirection: !isMobile ? 'row' : 'column-reverse', justifyContent: "center" }}>
      <Grid size={{ xs: 12, sm: 5, md: 5, lg: 12, xl: 5 }} sx={{ padding: '10px 10px', alignItems: "center", alignContent: "center", justifyContent: "center" }}>
        <ChartLegendGridItem
          container
          size={{ xs: 12 }}
          sx={{ ...(!isMobile ? {} : { justifyContent: 'center' }) }}
        >
          <ChartLegendBogItem>
            <ChartColorLegend color={colors.success400} />
            <Typography variant="body2">
              {t('dashboard.chart.firearms.legality.legal')}
              <Typography variant="subtitle2" sx={{ display: 'inline', paddingLeft: 1.5 }}>
                {legalityData?.[0]}
              </Typography>
            </Typography>
          </ChartLegendBogItem>
        </ChartLegendGridItem>

        <ChartLegendGridItem
          container
          size={{ xs: 12 }}
          sx={{
            ...(!isMobile ? {} : { justifyContent: 'center' }),
            ...(isDesktop && { minWidth: '190px' }),
          }}
        >
          <ChartLegendBogItem>
            <ChartColorLegend color={colors.gray500} />
            <Typography variant="body2">
              {t('dashboard.chart.firearms.legality.illegal')}
              <Typography variant="subtitle2" sx={{ display: 'inline', paddingLeft: 1.5 }}>
                {legalityData?.[1]}
              </Typography>
            </Typography>
          </ChartLegendBogItem>
        </ChartLegendGridItem>

        <ChartLegendGridItem
          size={{ xs: 12 }}
          sx={{ ...(!isMobile ? {} : { justifyContent: 'center', textAlign: 'center' }) }}
        >
          <Typography variant="body2">
            {t('dashboard.chart.firearms.legality.total').toUpperCase()}
            <Typography
              variant="subtitle2"
              component="p"
              sx={{ display: 'inline', paddingLeft: 1.5 }}
            >
              {totalValue}
            </Typography>
          </Typography>
        </ChartLegendGridItem>
      </Grid>

      <Grid
        size={{ xs: 12, sm: 5, md: 5, lg: 12, xl: 5 }}
        sx={{
          ...(!isMobile
            ? { display: 'block', alignSelf: 'center' }
            : { display: 'flex', justifyContent: 'center' }),
        }}
      >
        {chartData && (
          <ResponsiveContainerStyled width="100%" height={isDesktop ? 220 : 200}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="label"
                labelLine={false}
                animationDuration={900}
                label={({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
                  const radius = innerRadius + (outerRadius - innerRadius) / 2;
                  const RADIAN = Math.PI / 180;
                  const x = cx + radius * Math.cos(-(midAngle || 1) * RADIAN);
                  const y = cy + radius * Math.sin(-(midAngle || 1) * RADIAN);

                  return (
                    <text
                      x={x}
                      y={y}
                      fill="#fff"
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={14}
                      fontWeight={600}
                    >
                      {`${chartData[index].value}%`}
                    </text>
                  );
                }}
              >
                {chartData.map((entry: any) => (
                  <Cell key={entry.id} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainerStyled>
        )}
      </Grid>
    </Grid>
  );

  const noDataRender = (
    <ChartNoDataGridItem container size={{ xs: 12 }}>
      <Typography variant="body2">{t(noDataText)}</Typography>
    </ChartNoDataGridItem>
  );

  return (
    <DashboardChartWrapper>
      <Typography variant="subtitle1" sx={{ textAlign: 'center', marginBottom: 2 }}>
        {t('dashboard.chart.firearms.legality.label')}
      </Typography>
      {noData ? noDataRender : chartRender}

      <Box
        sx={{
          ...(!isMobile ? { padding: '20px 10px 0px 10px' } : { padding: '5px 10px 0 10px' }),
        }}
      >
        <Button
          variant={EButtonVariants.outlined}
          fullWidth
          label={t('dashboard.chart.firearms.legality.seeFirearms')}
          onClick={() => navigate(ERouteLinks.Firearms)}
          size={EButtonSizes.small}
        />
      </Box>
    </DashboardChartWrapper>
  );
};

export default FirearmsChart;
